// ═══════════════════════════════════════════════════════════════
// Team.jsx — XC-TEAM platform component (S7.A)
// Path: src/components/Team.jsx
//
// S7.A (May 21, 2026) — NEW FILE. Third platform component to ship
//                       (after XC-DOC at S5 and XC-MSG at S6).
//                       Renders the entity-level role matrix UI per
//                       sandbox v7.2 xcTeam reference (lines 7577-7720).
//
// ARCHITECTURE — Entity-level, current-data-model build
//   Reads /clientMembers filtered by workspaceId. Renders role rows
//   from clientMembers.roles[] array. Parcel-level role overrides
//   deferred to PA-XC-ENT-AND-PARCEL-ROLES-SPEC (post-S7).
//
//   This component:
//     1. Reads /clientMembers where workspaceId == X AND status == 'active'
//     2. Renders 9 conceptual role rows (per S7 Change Matrix Section 1)
//     3. Multiple owner_pm members → multiple rows in Owner AND PM slots
//        (compound role: owner_pm appears in both)
//     4. Empty slots render "Unassigned · Go to Marketplace" → S7.D toast
//     5. Tenant + vendor render empty state with Messages cross-link
//     6. NO parcel selector; Filter Level dropdown greys Parcel/Structure
//
// PROPS
//   workspace        — workspace object with workspaceId, label, type
//   roles            — current user's roles array in this workspace
//   roleLabel        — display label for current role
//   onSectionChange  — callback to switch ShellNav section (for Messages link)
//   mockMode         — if true, render hardcoded mock data
//                      (for ?xcteam-smoke=1 harness)
//
// CANONICAL SCHEMA: see Backend Schema v1 §clientMembers + S6 close
//   handoff §6 schemas. Reads only 4 universal fields:
//   userId, workspaceId, roles[], status. Forward-compat with future
//   override fields (ignored gracefully).
//
// FORWARD-COMPAT GUARDRAILS (per S7 Change Matrix Section 1)
//   #1  Reads ONLY /clientMembers. NEVER reads workspace.ownerUid.
//   #2  Owner row = all members with owner_pm OR owner_only.
//   #3  PM row = all members with owner_pm OR pm_only. Compound role
//       means owner_pm appears in BOTH Owner and PM rows.
//   #4  Empty role slot → "Unassigned · Go to Marketplace" → S7.D toast.
//   #5  Header shows entity name + type only. NO "Owner: X" line.
//   #6  owner_pm double-duty NOT fixed in S7 (PA-XC-ENT cleanup).
//   #7  Orphan UID fallback: render UID prefix if no /users record.
//   #8  Level filter: Entity enabled, Parcel/Structure greyed + tooltip.
//   #9  Forward-compat: read only the 4 universal fields.
//   #10 ONE workspace at a time. Portfolio view is not Team scope.
//   #11 PM+ full Team; tenant/vendor empty state with Messages cross-link.
//       Subset model REJECTED on privacy + scope grounds.
//
// AS REFERENCES
//   AS-S7-XC-ENT-SCOPE                  — entity-ownership consolidated input
//   AS-S7-NN-USEFILTEREDCOLLECTION-INLINE — inline hook for cleanup later
//   AS-S7-VSOWNER-UID                   — memory drift fix
//   AS-S7-SCHEMA-VARIANCE               — 3 clientMembers variants handled
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  collection, query, where, onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import XcFilterBar, { useFilterState } from './xcFilterBar';

// ── Role matrix definition (per S7 Change Matrix Section 1, option α) ────
// 9 conceptual role rows. Each row maps role label → role keys that fill it.
// Roles listed but not in current vocab render as "Unassigned" → invitation
// to fill via Marketplace (S7.D toast → S8 build).
const ROLE_MATRIX = [
  { key: 'owner',     label: 'Owner',                  matches: ['owner_pm', 'owner_only'] },
  { key: 'pm',        label: 'Property Manager',       matches: ['owner_pm', 'pm_only'] },
  { key: 'cm',        label: 'Construction Manager',   matches: ['cm'] },
  { key: 'maint',     label: 'Maintenance',            matches: ['maintenance'] },
  { key: 'tencoord',  label: 'Tenant Coordinator',     matches: ['tenant_coordinator'] },
  { key: 'vendor',    label: 'Vendor',                 matches: ['vendor'] },
  { key: 'sub',       label: 'Subcontractor',          matches: ['sub'] },
  { key: 'legal',     label: 'Legal',                  matches: ['legal'] },
  { key: 'books',     label: 'Bookkeeper',             matches: ['bookkeeper'] },
];

// Roles that should see the full Team matrix.
// Tenant + vendor + sub see the empty-state Messages CTA.
const PM_PLUS_ROLES = ['owner_pm', 'pm_only', 'cm', 'developer'];

// ── Inline useFilteredCollection (AS-S7-NN — promote in cleanup) ─────────
// Mirrors the local function in Maintain.jsx lines ~211-251.
// Reads /collectionName where (filterField) IN (filterValues) with onSnapshot.
function useFilteredCollection(collectionName, filterField, filterValue, extraFilter) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!filterValue) {
      setDocs([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const baseQuery = query(
      collection(db, collectionName),
      where(filterField, '==', filterValue)
    );
    const unsub = onSnapshot(
      baseQuery,
      (snap) => {
        let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (typeof extraFilter === 'function') {
          rows = rows.filter(extraFilter);
        }
        setDocs(rows);
        setLoading(false);
        setError(null);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error(`[useFilteredCollection ${collectionName}]`, err);
        setError(err);
        setLoading(false);
      }
    );
    return () => unsub();
    // Note: extraFilter intentionally omitted from deps — it's a closure
    // recreated each render, including it would loop. Pattern matches
    // Maintain.jsx useFilteredCollection (S5/S6 precedent).
  }, [collectionName, filterField, filterValue]);

  return { docs, loading, error };
}

// ── Mock data for harness mode (?xcteam-smoke=1) ─────────────────────────
const MOCK_MEMBERS = [
  { id: 'mockA_a1', userId: 'mockA', workspaceId: 'a1', roles: ['owner_pm'], status: 'active' },
  { id: 'mockB_a1', userId: 'mockB', workspaceId: 'a1', roles: ['owner_pm'], status: 'active' },
  { id: 'mockC_a1', userId: 'mockC', workspaceId: 'a1', roles: ['pm_only'], status: 'active' },
  { id: 'mockD_a1', userId: 'mockD', workspaceId: 'a1', roles: ['vendor'], status: 'active' },
  { id: 'mockE_a1', userId: 'mockE', workspaceId: 'a1', roles: ['sub'], status: 'active' },
];
const MOCK_WORKSPACE = { workspaceId: 'a1', label: 'A-1 Advanced Property Solutions LLC', type: 'LLC' };

// ── Display name helpers (Guardrail #7 — orphan UID fallback) ────────────
function uidPrefix(uid) {
  if (!uid || typeof uid !== 'string') return 'unknown';
  return uid.slice(0, 8);
}

function memberDisplayName(member) {
  // S7 has no /users read wired here (would require second query per member).
  // Render UID prefix as fallback. PA-XC-ENT spec phase: add /users lookup
  // or denormalize displayName onto clientMembers.
  return uidPrefix(member.userId);
}

// ── Styles (inline, matching Shell.jsx neutral professional aesthetic) ───
const styles = {
  page: {
    fontFamily: 'Arial, sans-serif',
    color: '#1F2937',
  },
  banner: {
    backgroundColor: '#F0F4F8',
    border: '1px solid #D5E8F0',
    borderRadius: 8,
    padding: '16px 20px',
    marginBottom: 20,
    fontSize: 13,
    color: '#003060',
    lineHeight: 1.5,
  },
  pageHead: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 16,
  },
  pageHeadLeft: {
    flex: '1 1 auto',
    minWidth: 280,
  },
  workspaceLabel: {
    fontSize: 22,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
  },
  workspaceType: {
    fontSize: 13,
    color: '#6B7280',
    marginTop: 4,
  },
  rolePill: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#EFF6FF',
    color: '#003060',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    marginTop: 8,
  },
  pageHeadRight: {
    flex: '0 0 auto',
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'right',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  kpiTile: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: '14px 16px',
  },
  kpiLabel: {
    fontSize: 11,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 700,
    color: '#003060',
  },
  kpiSub: {
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 4,
  },
  inheritanceNote: {
    fontSize: 12,
    color: '#6B7280',
    fontStyle: 'italic',
    marginBottom: 16,
    padding: '8px 12px',
    backgroundColor: '#FAFBFC',
    borderLeft: '3px solid #D5E8F0',
  },
  matrixWrap: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    overflow: 'hidden',
  },
  matrixRow: {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    borderBottom: '1px solid #F1F5F9',
    minHeight: 56,
  },
  matrixRowLast: {
    borderBottom: 'none',
  },
  roleLabelCell: {
    padding: '14px 16px',
    backgroundColor: '#FAFBFC',
    borderRight: '1px solid #F1F5F9',
    fontWeight: 600,
    fontSize: 13,
    color: '#003060',
    display: 'flex',
    alignItems: 'center',
  },
  membersCell: {
    padding: '12px 16px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  memberChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    backgroundColor: '#EFF6FF',
    border: '1px solid #D5E8F0',
    borderRadius: 16,
    fontSize: 12,
    color: '#003060',
    fontFamily: 'monospace',
  },
  unassignedButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    backgroundColor: '#FFFFFF',
    border: '1px dashed #CBD5E1',
    borderRadius: 16,
    fontSize: 12,
    color: '#6B7280',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    maxWidth: 360,
    padding: '12px 16px',
    backgroundColor: '#003060',
    color: '#FFFFFF',
    borderRadius: 8,
    fontSize: 13,
    lineHeight: 1.5,
    boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
    zIndex: 9999,
  },
  emptyStateWrap: {
    padding: 48,
    textAlign: 'center',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#003060',
    marginBottom: 8,
  },
  emptyStateBody: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 20,
    maxWidth: 420,
    margin: '0 auto 20px',
    lineHeight: 1.6,
  },
  emptyStateCta: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#003060',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  loadingState: {
    padding: 48,
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: 14,
  },
  errorState: {
    padding: 24,
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    color: '#991B1B',
    fontSize: 13,
  },
};

// ── Main component ───────────────────────────────────────────────────────

export default function Team({ workspace, roles, roleLabel, onSectionChange, mockMode = false }) {
  const [toastMessage, setToastMessage] = useState(null);

  // Determine PM+ access (Guardrail #11)
  const isPmPlus = useMemo(() => {
    if (!Array.isArray(roles)) return false;
    return roles.some((r) => PM_PLUS_ROLES.includes(r));
  }, [roles]);

  // Filter state — consumes xc-team viewId from xcFilterBar.js
  const filterState = useFilterState('xc-team');

  // Live data — reads /clientMembers (skipped in mockMode)
  const workspaceId = workspace?.workspaceId;
  const live = useFilteredCollection(
    'clientMembers',
    'workspaceId',
    mockMode ? null : workspaceId,
    (row) => row.status === 'active'
  );

  const members = mockMode ? MOCK_MEMBERS : live.docs;
  const loading = mockMode ? false : live.loading;
  const error = mockMode ? null : live.error;
  const effectiveWorkspace = mockMode ? MOCK_WORKSPACE : workspace;

  // Group members by role row (per ROLE_MATRIX above)
  // A single member can appear in MULTIPLE rows if their roles[] includes
  // multiple matching role keys — this is correct per Guardrail #3.
  const roleAssignments = useMemo(() => {
    const map = {};
    ROLE_MATRIX.forEach((row) => {
      map[row.key] = members.filter((m) => {
        const memberRoles = Array.isArray(m.roles) ? m.roles : [];
        return memberRoles.some((r) => row.matches.includes(r));
      });
    });
    return map;
  }, [members]);

  // Filter application — Fill filter (All / Filled / Unassigned)
  const visibleRows = useMemo(() => {
    const fill = filterState.values?.fill || 'All';
    return ROLE_MATRIX.filter((row) => {
      const count = roleAssignments[row.key]?.length || 0;
      if (fill === 'Filled') return count > 0;
      if (fill === 'Unassigned') return count === 0;
      return true; // All
    });
  }, [filterState.values, roleAssignments]);

  // KPI calculations
  const kpis = useMemo(() => {
    const totalSlots = ROLE_MATRIX.length;
    const filledSlots = ROLE_MATRIX.filter(
      (row) => (roleAssignments[row.key]?.length || 0) > 0
    ).length;
    const completeness = totalSlots > 0
      ? Math.round((filledSlots / totalSlots) * 100)
      : 0;
    const totalAssignments = members.length;
    const pmFilled = (roleAssignments.pm?.length || 0) > 0;
    return { totalSlots, filledSlots, completeness, totalAssignments, pmFilled };
  }, [roleAssignments, members]);

  // Workspace display name — fall through label → workspaceName → fallback.
  // useWorkspace hook surfaces workspaceName; Firestore docs also carry label.
  // Either field is acceptable identity per S6 close conventions.
  const workspaceDisplayName = (
    effectiveWorkspace?.label ||
    effectiveWorkspace?.workspaceName ||
    effectiveWorkspace?.name ||
    'this workspace'
  );

  // S7.D toast handler — Marketplace cross-link stub
  const fireToast = useCallback((roleLabel) => {
    setToastMessage(
      `Marketplace coming in S8 — would let you fill the ${roleLabel} role ` +
      `for ${workspaceDisplayName} by searching the provider network or ` +
      `inviting someone you already know.`
    );
    setTimeout(() => setToastMessage(null), 5000);
  }, [workspaceDisplayName]);

  // Messages cross-link handler — empty-state CTA for tenant/vendor
  const goToMessages = useCallback(() => {
    if (typeof onSectionChange === 'function') {
      onSectionChange('xc-messages');
    }
  }, [onSectionChange]);

  // ── Tenant / Vendor empty state (Guardrail #11) ──────────────────────
  if (!mockMode && !isPmPlus) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyStateWrap}>
          <div style={styles.emptyStateTitle}>Your team contacts</div>
          <div style={styles.emptyStateBody}>
            Your team contacts will appear here. Contact your property manager
            directly through Messages for now.
          </div>
          <button
            type="button"
            style={styles.emptyStateCta}
            onClick={goToMessages}
            aria-label="Open Messages"
          >
            Open Messages
          </button>
        </div>
      </div>
    );
  }

  // ── PM+ full Team view ───────────────────────────────────────────────
  return (
    <div style={styles.page}>
      {/* Banner — XC-TEAM context (Tier 2 universal shell) */}
      <div style={styles.banner}>
        <strong>Team</strong> — Role assignments for {workspaceDisplayName}.
        Empty slots are roles you can fill through the Marketplace (coming S8) or by inviting
        providers you already work with.
      </div>

      {/* Page head — workspace identity */}
      <div style={styles.pageHead}>
        <div style={styles.pageHeadLeft}>
          <h2 style={styles.workspaceLabel}>
            {workspaceDisplayName}
          </h2>
          <div style={styles.workspaceType}>
            {effectiveWorkspace?.type || effectiveWorkspace?.kind || 'Entity'} ·{' '}
            {kpis.totalAssignments} {kpis.totalAssignments === 1 ? 'member' : 'members'}
          </div>
          {roleLabel && <div style={styles.rolePill}>You: {roleLabel}</div>}
        </div>
        <div style={styles.pageHeadRight}>
          XC-TEAM · Tier 2<br />
          Entity-level view
        </div>
      </div>

      {/* Filter bar — xc-team viewId (Entity enabled; Parcel/Structure greyed) */}
      <XcFilterBar viewId="xc-team" filterState={filterState} />

      {/* KPI grid */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiTile}>
          <div style={styles.kpiLabel}>Team Completeness</div>
          <div style={styles.kpiValue}>{kpis.completeness}%</div>
          <div style={styles.kpiSub}>{kpis.filledSlots} of {kpis.totalSlots} role slots</div>
        </div>
        <div style={styles.kpiTile}>
          <div style={styles.kpiLabel}>Active Members</div>
          <div style={styles.kpiValue}>{kpis.totalAssignments}</div>
          <div style={styles.kpiSub}>across this entity</div>
        </div>
        <div style={styles.kpiTile}>
          <div style={styles.kpiLabel}>PM Coverage</div>
          <div style={styles.kpiValue}>{kpis.pmFilled ? 'Yes' : 'No'}</div>
          <div style={styles.kpiSub}>
            {kpis.pmFilled ? 'PM assigned' : 'No PM — self-managed or unfilled'}
          </div>
        </div>
        <div style={styles.kpiTile}>
          <div style={styles.kpiLabel}>Unassigned Roles</div>
          <div style={styles.kpiValue}>{kpis.totalSlots - kpis.filledSlots}</div>
          <div style={styles.kpiSub}>fill via Marketplace (S8)</div>
        </div>
      </div>

      {/* Inheritance note — Strategy §3f entity→parcel→structure */}
      <div style={styles.inheritanceNote}>
        Showing entity-level roles. Per-parcel and per-structure role overrides
        coming in a future release (PA-XC-ENT-AND-PARCEL-ROLES-SPEC).
      </div>

      {/* Role matrix */}
      {loading && <div style={styles.loadingState}>Loading team members…</div>}
      {error && (
        <div style={styles.errorState}>
          Could not load team members: {String(error.message || error)}
        </div>
      )}
      {!loading && !error && (
        <div style={styles.matrixWrap}>
          {visibleRows.map((row, idx) => {
            const assignedMembers = roleAssignments[row.key] || [];
            const isLast = idx === visibleRows.length - 1;
            const rowStyle = isLast
              ? { ...styles.matrixRow, ...styles.matrixRowLast }
              : styles.matrixRow;
            return (
              <div key={row.key} style={rowStyle}>
                <div style={styles.roleLabelCell}>{row.label}</div>
                <div style={styles.membersCell}>
                  {assignedMembers.length === 0 ? (
                    <button
                      type="button"
                      style={styles.unassignedButton}
                      onClick={() => fireToast(row.label)}
                      aria-label={`Fill ${row.label} role via Marketplace`}
                    >
                      Unassigned · Go to Marketplace
                    </button>
                  ) : (
                    assignedMembers.map((member) => (
                      <span key={member.id} style={styles.memberChip}>
                        {memberDisplayName(member)}
                      </span>
                    ))
                  )}
                </div>
              </div>
            );
          })}
          {visibleRows.length === 0 && (
            <div style={{ ...styles.loadingState, padding: 32 }}>
              No role rows match the current filter.
            </div>
          )}
        </div>
      )}

      {/* Toast (S7.D Marketplace stub) */}
      {toastMessage && (
        <div role="status" aria-live="polite" style={styles.toast}>
          {toastMessage}
        </div>
      )}
    </div>
  );
}
